import React, { useState, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Row, Col, Spinner, Button } from "react-bootstrap";
import { FaSave, FaPaperPlane, FaArrowLeft, FaUpload, FaTimes, FaMagic } from "react-icons/fa";
import { createBlog, uploadBlogImage } from "../utils/blogApi";
import { toast } from "react-toastify";
import BlogEditor from "./BlogEditor";
import BlogCoverCropModal from "./BlogCoverCropModal";

export default function BlogCreate() {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);

  const [formData, setFormData] = useState({
    title: "",
    content: "",
    excerpt: "",
    coverImage: "",
    tags: [],
    type: "general",
    seo: { metaTitle: "", metaDescription: "" },
  });
  const [tagInput, setTagInput] = useState("");
  const [saving, setSaving] = useState(false);
  const [uploadingCover, setUploadingCover] = useState(false);
  const [isGeneratingContent, setIsGeneratingContent] = useState(false);
  const [showCropModal, setShowCropModal] = useState(false);
  const [cropImageSrc, setCropImageSrc] = useState(null);

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSeoChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      seo: { ...prev.seo, [field]: value },
    }));
  };

  const handleAddTag = (e) => {
    if (e.key === "Enter" && tagInput.trim()) {
      e.preventDefault();
      const tag = tagInput.trim().toLowerCase();
      if (!formData.tags.includes(tag)) {
        setFormData((prev) => ({ ...prev, tags: [...prev.tags, tag] }));
      }
      setTagInput("");
    }
  };

  const handleRemoveTag = (tagToRemove) => {
    setFormData((prev) => ({
      ...prev,
      tags: prev.tags.filter((t) => t !== tagToRemove),
    }));
  };

  const generateAIContent = async () => {
    if (!formData.title.trim()) {
      toast.error("Please enter a title first to generate content");
      return;
    }

    try {
      setIsGeneratingContent(true);

      const response = await fetch("/api/generate-blog-content", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          title: formData.title,
          excerpt: formData.excerpt,
          tags: formData.tags,
          type: formData.type,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Failed to generate content");
      }

      const data = await response.json();

      // Convert plain text paragraphs to HTML for the editor
      const paragraphs = data.content.split("\n\n").filter((p) => p.trim());
      const htmlContent = paragraphs.map((p) => `<p>${p.trim()}</p>`).join("");

      handleChange("content", htmlContent);
      toast.success("AI-generated content added!");
    } catch (error) {
      console.error("Error generating content:", error);
      toast.error("Failed to generate content. Please try again.");
    } finally {
      setIsGeneratingContent(false);
    }
  };

  const handleCoverSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (file.size > 5 * 1024 * 1024) {
      toast.error("Cover image must be less than 5MB");
      return;
    }

    const validTypes = ["image/jpeg", "image/png", "image/gif", "image/webp"];
    if (!validTypes.includes(file.type)) {
      toast.error("Please upload a valid image (JPEG, PNG, GIF, WebP)");
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      setCropImageSrc(reader.result);
      setShowCropModal(true);
    };
    reader.readAsDataURL(file);
    // Reset the file input so the same file can be re-selected
    e.target.value = "";
  };

  const handleCropDone = useCallback(async (croppedBlob) => {
    try {
      setUploadingCover(true);
      const imageUrl = await uploadBlogImage(croppedBlob);
      handleChange("coverImage", imageUrl);
      toast.success("Cover image uploaded");
      setShowCropModal(false);
      setCropImageSrc(null);
    } catch (err) {
      console.error("Cover upload error:", err);
      toast.error("Failed to upload cover image");
    } finally {
      setUploadingCover(false);
    }
  }, []);

  const handleCropCancel = useCallback(() => {
    setShowCropModal(false);
    setCropImageSrc(null);
  }, []);

  const handleSubmit = async (status) => {
    if (!formData.title.trim()) {
      toast.error("Title is required");
      return;
    }
    if (!formData.content.trim()) {
      toast.error("Content is required");
      return;
    }

    try {
      setSaving(true);
      const data = await createBlog({ ...formData, status });
      toast.success(
        status === "published"
          ? "Blog published successfully!"
          : "Blog saved as draft"
      );
      navigate("/dashboard/blog");
    } catch (err) {
      console.error("Error creating blog:", err);
      toast.error(err.message || "Failed to create blog");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="blog-admin-form">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div className="d-flex align-items-center gap-3">
          <button
            className="btn btn-outline-secondary btn-sm"
            onClick={() => navigate("/dashboard/blog")}
          >
            <FaArrowLeft /> Back
          </button>
          <h1 className="page-title mb-0">Create Blog Post</h1>
        </div>
        <div className="d-flex gap-2">
          <button
            className="btn btn-outline-secondary d-flex align-items-center gap-2"
            onClick={() => handleSubmit("draft")}
            disabled={saving}
          >
            <FaSave /> Save Draft
          </button>
          <button
            className="btn btn-primary d-flex align-items-center gap-2"
            onClick={() => handleSubmit("published")}
            disabled={saving}
          >
            {saving ? (
              <Spinner animation="border" size="sm" />
            ) : (
              <FaPaperPlane />
            )}
            Publish
          </button>
        </div>
      </div>

      <Row>
        <Col lg={8}>
          {/* Title */}
          <div className="mb-3">
            <label className="form-label fw-semibold">Title *</label>
            <input
              type="text"
              className="form-control form-control-lg"
              placeholder="Enter blog title..."
              value={formData.title}
              onChange={(e) => handleChange("title", e.target.value)}
            />
          </div>

          {/* Content Editor */}
          <div className="mb-3">
            <div className="d-flex justify-content-between align-items-center mb-2">
              <label className="form-label fw-semibold mb-0">Content *</label>
              <Button
                variant="outline-primary"
                size="sm"
                onClick={generateAIContent}
                disabled={isGeneratingContent || !formData.title.trim()}
                className="d-flex align-items-center gap-2"
              >
                {isGeneratingContent ? (
                  <>
                    <Spinner as="span" animation="border" size="sm" />
                    <span>Writing...</span>
                  </>
                ) : (
                  <>
                    <FaMagic />
                    <span>AI Write Description</span>
                  </>
                )}
              </Button>
            </div>
            {!formData.title.trim() && (
              <small className="text-muted d-block mb-2">
                Enter a title first to enable AI content generation
              </small>
            )}
            <BlogEditor
              content={formData.content}
              onChange={(val) => handleChange("content", val)}
            />
          </div>

          {/* Excerpt */}
          <div className="mb-3">
            <label className="form-label fw-semibold">Excerpt</label>
            <textarea
              className="form-control"
              rows={3}
              placeholder="Short summary of the blog post..."
              value={formData.excerpt}
              onChange={(e) => handleChange("excerpt", e.target.value)}
              maxLength={300}
            />
            <small className="text-muted">
              {formData.excerpt.length}/300 characters
            </small>
          </div>
        </Col>

        <Col lg={4}>
          {/* Cover Image */}
          <div className="mb-3 blog-admin-sidebar-card">
            <label className="form-label fw-semibold">Cover Image</label>
            {formData.coverImage ? (
              <div className="blog-cover-preview">
                <img src={formData.coverImage} alt="Cover preview" />
                <button
                  className="blog-cover-remove"
                  onClick={() => handleChange("coverImage", "")}
                >
                  <FaTimes />
                </button>
              </div>
            ) : (
              <div
                className="blog-cover-upload"
                onClick={() => fileInputRef.current?.click()}
              >
                {uploadingCover ? (
                  <Spinner animation="border" size="sm" />
                ) : (
                  <>
                    <FaUpload size={24} />
                    <p className="mb-0 mt-2">Click to upload cover image</p>
                    <small className="text-muted">Max 5MB · JPEG, PNG, GIF, WebP</small>
                  </>
                )}
              </div>
            )}
            <input
              ref={fileInputRef}
              type="file"
              accept="image/jpeg,image/png,image/gif,image/webp"
              onChange={handleCoverSelect}
              style={{ display: "none" }}
            />
            <BlogCoverCropModal
              show={showCropModal}
              imageSrc={cropImageSrc}
              onCropDone={handleCropDone}
              onCancel={handleCropCancel}
              uploading={uploadingCover}
            />
          </div>

          {/* Type */}
          <div className="mb-3 blog-admin-sidebar-card">
            <label className="form-label fw-semibold">Type / Category</label>
            <input
              type="text"
              className="form-control"
              placeholder="e.g., fishing-tips, guides, news"
              value={formData.type}
              onChange={(e) => handleChange("type", e.target.value)}
            />
          </div>

          {/* Tags */}
          <div className="mb-3 blog-admin-sidebar-card">
            <label className="form-label fw-semibold">Tags</label>
            <input
              type="text"
              className="form-control"
              placeholder="Type a tag and press Enter"
              value={tagInput}
              onChange={(e) => setTagInput(e.target.value)}
              onKeyDown={handleAddTag}
            />
            {formData.tags.length > 0 && (
              <div className="blog-tags-list mt-2">
                {formData.tags.map((tag) => (
                  <span key={tag} className="blog-tag-badge">
                    {tag}
                    <button onClick={() => handleRemoveTag(tag)}>
                      <FaTimes />
                    </button>
                  </span>
                ))}
              </div>
            )}
          </div>

          {/* SEO */}
          <div className="mb-3 blog-admin-sidebar-card">
            <label className="form-label fw-semibold">SEO Settings</label>
            <div className="mb-2">
              <small className="text-muted">Meta Title</small>
              <input
                type="text"
                className="form-control form-control-sm"
                placeholder="SEO title (defaults to blog title)"
                value={formData.seo.metaTitle}
                onChange={(e) => handleSeoChange("metaTitle", e.target.value)}
                maxLength={70}
              />
              <small className="text-muted">
                {formData.seo.metaTitle.length}/70
              </small>
            </div>
            <div>
              <small className="text-muted">Meta Description</small>
              <textarea
                className="form-control form-control-sm"
                rows={3}
                placeholder="SEO description (defaults to excerpt)"
                value={formData.seo.metaDescription}
                onChange={(e) =>
                  handleSeoChange("metaDescription", e.target.value)
                }
                maxLength={160}
              />
              <small className="text-muted">
                {formData.seo.metaDescription.length}/160
              </small>
            </div>
          </div>
        </Col>
      </Row>
    </div>
  );
}
